/**
 * SES Sandbox — Secure code execution via Compartments
 *
 * All AI-generated Office.js code runs in a locked-down SES Compartment.
 * Blocks dangerous constructors (Function, Reflect, Proxy) and
 * sensitive Office.js APIs (insertSlidesFromBase64, openBrowserWindow, etc.).
 *
 * Based on: hewliyang/open-excel sandbox.ts
 */

import { ensureLockdown } from './lockdown';

/* global globalThis */

/**
 * List of Office.js APIs that must NOT be accessible from sandboxed code.
 *
 * Two-layer defense:
 *  1. Source string match (checkForBlockedOperations) — fast pre-flight reject
 *     of code that mentions a blocked name literally.
 *  2. Capability facade (denyBlockedCapabilities) — wraps the injected Office
 *     objects in a Proxy that HARD-DENIES these methods at INVOCATION time,
 *     regardless of how the name is referenced or spelled. This closes the
 *     runtime-string-assembly bypass (e.g. obj["insertSlides" + "FromBase64"])
 *     that a static source scan cannot see.
 */
const BLOCKED_OPERATIONS = [
  'openBrowserWindow',
  'displayDialogAsync',
  'getAccessToken',
  'insertSlidesFromBase64',     // Prevent importing external content (PowerPoint)
  'insertWorksheetsFromBase64', // Prevent importing external content (Excel)
  'insertFileFromBase64',       // Prevent importing external content (Word/Excel)
  'valuesAsJson',               // Excel: blocked write vector (data exfiltration)
  'valuesAsJsonLocal',          // Excel: blocked write vector (data exfiltration)
  'sendMail',                   // Prevent email exfiltration
  'getFileAsync',               // Prevent file system access
  'saveAsync',                  // Prevent unauthorized saves
];

/**
 * Method names that must be DENIED at invocation time on any injected Office
 * object, no matter how they are reached through the object graph. This is the
 * capability set the facade enforces; it is a subset of BLOCKED_OPERATIONS
 * limited to the property names a sandbox could actually resolve on the live
 * `context` / host-namespace graph that is handed in as a global.
 *
 * Keep this aligned with the source-string denylist above. The two layers are
 * intentionally redundant (defense in depth): the string scan rejects literal
 * mentions early, the facade catches anything assembled at run time.
 */
const BLOCKED_CAPABILITY_NAMES = new Set(BLOCKED_OPERATIONS);

// Sentinel that both marks a facade and, when read, yields the genuine target
// it wraps. Used to detect an existing facade and to UNWRAP a facade that
// sandboxed code passes back into an Office.js method as an argument, so the
// host receives its own raw object (not our proxy) and its internal
// identity/slot checks keep working.
const FACADE_MARKER = Symbol('restackCapabilityFacade');

/**
 * Return the genuine target a facade wraps, or the value itself if it is not a
 * facade. Lets the apply trap hand Office.js raw objects for its arguments.
 * @param {*} value - A possibly-faceted value
 * @returns {*} The unwrapped target, or the value unchanged
 */
function unwrapFacade(value) {
  if (value === null || (typeof value !== 'object' && typeof value !== 'function')) {
    return value;
  }
  try {
    const raw = value[FACADE_MARKER];
    return raw !== undefined ? raw : value;
  } catch {
    return value;
  }
}

/**
 * Wrap a live Office object in a capability facade.
 *
 * The facade is a transparent read-through Proxy: every property access returns
 * the underlying value, but
 *  - a blocked method name (BLOCKED_CAPABILITY_NAMES) resolves to a function
 *    that throws on call AND cannot be read as a value reference, and
 *  - any nested object/function returned by a `get` is itself wrapped, so the
 *    denial holds at EVERY depth of the object graph regardless of how a name
 *    is reached or spelled.
 *
 * Real methods are bound to the genuine target (not the proxy) before being
 * returned, so Office.js's internal `this`-identity and request-batching are
 * preserved and legitimate calls keep working untouched.
 *
 * Primitives are returned as-is (nothing to wrap). Failures to wrap fall back
 * to denying access (fail closed) rather than exposing the raw object.
 *
 * @param {*} obj - Object/function to wrap (or any value; primitives pass through)
 * @param {WeakMap} [seen] - Cache so a given target maps to a stable facade
 * @returns {*} The facade, or the original value if it is a primitive
 */
function denyBlockedCapabilities(obj, seen = new WeakMap()) {
  // Primitives (and null) carry no methods to abuse — return unchanged.
  if (obj === null || (typeof obj !== 'object' && typeof obj !== 'function')) {
    return obj;
  }

  // Already a facade, or already wrapped this exact target: reuse it. This
  // keeps object identity stable across repeated reads and avoids unbounded
  // proxy nesting on cyclic Office.js graphs.
  try {
    if (obj[FACADE_MARKER]) return obj;
  } catch {
    // Reading the marker threw (exotic host object). Fail closed below.
    return createDeniedAccessTarget();
  }
  const cached = seen.get(obj);
  if (cached) return cached;

  let proxy;
  try {
    proxy = new globalThis.Proxy(obj, {
      get(target, prop) {
        // Reading the marker yields the genuine target (for unwrapping).
        if (prop === FACADE_MARKER) return target;

        // Hard-deny blocked capabilities at the moment they are read. A string
        // property whose name is blocked never yields the real method; it
        // yields a thunk that throws on call, so neither invoking it nor
        // smuggling the reference out works.
        if (typeof prop === 'string' && BLOCKED_CAPABILITY_NAMES.has(prop)) {
          return () => {
            throw new Error(
              `Blocked operation: ${prop} is not allowed in sandboxed code`
            );
          };
        }

        // Read against the genuine target (not the proxy) so any Office.js
        // navigation getter runs with its real `this` and the host's request
        // batching is untouched.
        const value = Reflect.get(target, prop, target);

        // Bind real methods to the genuine target so Office.js keeps its
        // internal `this` identity and request batching, then wrap the bound
        // function so blocked names hanging off it are denied too.
        if (typeof value === 'function') {
          const bound = value.bind(target);
          return denyBlockedCapabilities(bound, seen);
        }

        // Recurse into nested objects so the denial holds at every depth.
        if (value !== null && typeof value === 'object') {
          return denyBlockedCapabilities(value, seen);
        }

        return value;
      },
      has(target, prop) {
        // A blocked name is reported absent so `in` checks and feature probes
        // cannot be used to branch toward an alternate access path.
        if (typeof prop === 'string' && BLOCKED_CAPABILITY_NAMES.has(prop)) {
          return false;
        }
        return Reflect.has(target, prop);
      },
      apply(target, thisArg, args) {
        // Unwrap any faceted arguments so Office.js receives its own raw objects
        // (e.g. range.copyFrom(otherRange), charts.add(type, sourceRange)) and
        // its internal identity/slot checks are not confused by our proxy.
        const rawArgs = args.map(unwrapFacade);
        // Re-wrap the return value of a method call so the denial also holds on
        // objects produced by methods (e.g. a navigation helper that returns a
        // child object), not only on objects reached through property reads.
        const result = Reflect.apply(target, thisArg, rawArgs);
        // Pass Promises through unwrapped: they expose no Office capability, and
        // wrapping them would proxy the whole await chain (e.g. context.sync())
        // for no security gain.
        if (result && typeof result.then === 'function') return result;
        return denyBlockedCapabilities(result, seen);
      },
    });
  } catch {
    // Proxy construction failed (e.g. a revoked/exotic host object). Fail
    // closed: deny access entirely rather than leak the raw object.
    return createDeniedAccessTarget();
  }

  seen.set(obj, proxy);
  return proxy;
}

/**
 * A stand-in returned when an object cannot be safely faceted. Every property
 * access throws, so a wrapping failure can never silently expose a raw,
 * unguarded Office object to sandboxed code.
 * @returns {Proxy}
 */
function createDeniedAccessTarget() {
  const deny = () => {
    throw new Error('Blocked operation: capability facade unavailable for this object');
  };
  return new globalThis.Proxy(Object.create(null), {
    get() { return deny; },
    has() { return false; },
    apply() { return deny(); },
  });
}

/**
 * Create a restricted Object global that strips dangerous methods.
 * Stronger than string-matching: these methods are simply not available
 * in the sandbox, preventing prototype manipulation and descriptor abuse.
 * @returns {Object} Restricted Object constructor
 */
function createSafeObject() {
  const safe = Object.create(null);
  // Copy safe methods from Object
  const allowed = [
    'keys', 'values', 'entries', 'assign', 'freeze',
    'fromEntries', 'is', 'hasOwn', 'create',
  ];
  for (const key of allowed) {
    if (typeof Object[key] === 'function') {
      safe[key] = Object[key].bind(Object);
    }
  }
  // Explicitly omit: defineProperty, defineProperties, getOwnPropertyDescriptor,
  // getOwnPropertyDescriptors, getPrototypeOf, setPrototypeOf
  return safe;
}

/**
 * Check if code contains blocked operations.
 * @param {string} code - Code to check
 * @throws {Error} if blocked operation found
 */
function checkForBlockedOperations(code) {
  for (const op of BLOCKED_OPERATIONS) {
    if (code.includes(op)) {
      throw new Error(`Blocked operation: ${op} is not allowed in sandboxed code`);
    }
  }
}

/**
 * Globals that are live Office object graphs (the request `context` and the
 * host namespaces). These are the only injected values that can reach a blocked
 * Office API, so only these get the capability facade. Plain helpers exposed to
 * the OOXML path (zip, markDirty, escapeXml, DOMParser, XMLSerializer,
 * findShapeByName) carry none of the blocked methods and are left untouched so
 * `new DOMParser()` and friends keep working exactly as before.
 */
const OFFICE_GRAPH_GLOBALS = new Set([
  'context',
  'PowerPoint',
  'Excel',
  'Word',
  'Office',
]);

/**
 * Apply the capability facade to the Office object-graph globals, returning a
 * fresh globals object safe to hand to the Compartment. Non-Office globals pass
 * through unchanged. A shared `seen` WeakMap keeps a stable facade identity if
 * the same underlying object is exposed under more than one name.
 * @param {Object} globals - The raw globals about to be injected
 * @returns {Object} A new globals object with Office graphs faceted
 */
function facadeOfficeGlobals(globals) {
  if (!globals || typeof globals !== 'object') return globals;
  const seen = new WeakMap();
  const out = {};
  for (const key of Object.keys(globals)) {
    const value = globals[key];
    out[key] = OFFICE_GRAPH_GLOBALS.has(key)
      ? denyBlockedCapabilities(value, seen)
      : value;
  }
  return out;
}

/**
 * Execute code in a sandboxed SES Compartment.
 *
 * @param {string} code - JavaScript code (async function body)
 * @param {Object} globals - Variables to expose to the compartment
 * @returns {Promise<any>} - Result of code execution
 */
export async function sandboxedEval(code, globals) {
  await ensureLockdown();

  // Pre-flight security check
  checkForBlockedOperations(code);

  // Compartment is added to globalThis by SES after lockdown
  const Compartment = globalThis.Compartment;
  if (!Compartment) {
    throw new Error('SES Compartment not available after lockdown');
  }

  // Capability enforcement: wrap the live Office object graphs so blocked
  // methods are denied at invocation time, regardless of how they are spelled
  // or assembled at run time. This is the runtime backstop to the source-string
  // scan above. Proxy is read from the real globalThis here (it is only stripped
  // from the Compartment's own globals below), so sandboxed code still cannot
  // construct its own Proxy.
  const safeGlobals = facadeOfficeGlobals(globals);

  const compartment = new Compartment({
    globals: {
      ...safeGlobals,
      console,
      Math,
      Date,
      JSON,
      parseInt,
      parseFloat,
      isNaN,
      isFinite,
      encodeURIComponent,
      decodeURIComponent,
      encodeURI,
      decodeURI,
      Array,
      Object: createSafeObject(),
      String,
      Number,
      Boolean,
      Map,
      Set,
      WeakMap,
      WeakSet,
      Promise,
      Symbol,
      RegExp,
      Error,
      TypeError,
      RangeError,
      URIError,
      // Blocked constructors
      Function: undefined,
      Reflect: undefined,
      Proxy: undefined,
      Compartment: undefined,
      harden: undefined,
      lockdown: undefined,
      eval: undefined,
    },
    __options__: true,
  });

  return compartment.evaluate(`(async () => { ${code} })()`);
}
